/**
 * Admin Layout Component
 * 
 * Main layout wrapper for authenticated admin pages.
 * Provides consistent structure:
 * - Header at the top
 * - Navigation bar below header
 * - White content area for page content
 * - Footer at the bottom
 * 
 * This component wraps all authenticated admin pages to ensure
 * consistent UI structure across the admin interface.
 */
import React from 'react';
import AdminHeader from '../header/header';
import AdminNavbar, { NavTab } from '../navBar/navbar';
import AdminFooter from '../footer/footer';
import './AdminLayout.css';

export interface AdminLayoutProps {
  /** Content to render in the white content area */
  children: React.ReactNode;
  /** User name to display in header */
  userName?: string;
  /** Avatar URL for header */
  avatarUrl?: string;
  /** Active tab in navbar */
  activeTab?: NavTab;
  /** Callback when navbar tab changes */
  onTabChange?: (tab: NavTab) => void;
  /** Whether to use white background for content area (default: true, false for dashboard) */
  useWhiteBackground?: boolean;
  /** Logout handler shown in the header next to the profile name */
  onLogout?: () => void;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({
  children,
  userName,
  avatarUrl,
  activeTab = 'dashboard',
  onTabChange,
  useWhiteBackground = true,
  onLogout,
}) => {
  return (
    <div className="admin-layout">
      {/* Header bar at the top */}
      <AdminHeader userName={userName} avatarUrl={avatarUrl} onLogout={onLogout} />
      
      {/* Navigation bar below header */}
      <AdminNavbar activeTab={activeTab} onTabChange={onTabChange} />
      
      {/* Content area - white background for most pages, transparent for dashboard */}
      <main className={`admin-layout__content ${
        !useWhiteBackground ? 'admin-layout__content--no-white' : ''
      }`}>
        {children}
      </main>
      
      {/* Footer at the bottom */}
      <AdminFooter />
    </div>
  );
};

export default AdminLayout;
